import {
  configureStore,
  ThunkAction,
  Action,
  combineReducers,
  Middleware,
} from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';

import { useEnvironment, usePanorama } from '@wix/yoshi-flow-editor';
import type {
  OwnerLogger,
  VisitorLogger,
  TFunction,
  IHttpClient,
  Experiments,
} from '@wix/yoshi-flow-editor';

import { DuplexerConnection } from '@/services/duplexer';
import { SiteEcomService } from '@/services/siteEcom';

import {
  messagesReducer,
  layoutReducer,
  flagsReducer,
  geoLocationReducer,
  conversationsQuotaReducer,
  oneChatSettingsReducer,
} from '../features';

import { sessionVisualLayoutMiddleware } from './middlewares';

export const logger = createLogger({
  predicate: (_, action) => true,
  collapsed: true,
  diff: true,
}) as Middleware;

export const reducer = combineReducers({
  messages: messagesReducer,
  layout: layoutReducer,
  oneChatSettings: oneChatSettingsReducer,
  flags: flagsReducer,
  geoLocation: geoLocationReducer,
  conversationsQuota: conversationsQuotaReducer,
});

export type GetStoreParams = {
  httpClient: IHttpClient;
  panorama: ReturnType<typeof usePanorama>;
  t: TFunction;
  bi: VisitorLogger;
  duplexerConnection: DuplexerConnection | null;
  siteEcom: SiteEcomService;
  experiments: Experiments;
  environment: ReturnType<typeof useEnvironment>;
  preloadedState?: Partial<RootState>;
  needLogger: boolean;
  needDevTools: boolean;
};

export const getStore = ({
  httpClient,
  panorama,
  t,
  bi,
  duplexerConnection,
  siteEcom,
  experiments,
  environment,
  preloadedState,
  needLogger,
  needDevTools,
}: GetStoreParams) =>
  configureStore({
    preloadedState,
    reducer,
    middleware: (getDefaultMiddleware) => {
      const middlewares = getDefaultMiddleware({
        thunk: {
          extraArgument: {
            httpClient,
            panorama,
            t,
            bi,
            duplexerConnection,
            siteEcom,
            experiments,
            environment,
          } satisfies ThunkExtraArgument,
        },
        serializableCheck: false,
      }).prepend(sessionVisualLayoutMiddleware);
      return needLogger ? middlewares.concat(logger) : middlewares;
    },
    devTools: needDevTools,
  });

export type AppStore = ReturnType<typeof getStore>;
export type AppDispatch = ReturnType<typeof getStore>['dispatch'];
export type RootState = ReturnType<typeof reducer>;
export interface ThunkExtraArgument {
  httpClient: IHttpClient;
  panorama: ReturnType<typeof usePanorama>;
  duplexerConnection: DuplexerConnection | null;
  siteEcom: SiteEcomService;
  experiments: Experiments;
  environment: ReturnType<typeof useEnvironment>;
  t: TFunction;
  bi: OwnerLogger;
}
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  ThunkExtraArgument,
  Action<string>
>;
